import * as React from 'react';
import { Loader2, Save } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { TotadminPermission } from 'data/__generated__';
import { PageActionsMenu, type PageActionGroup } from './PageActionsMenu';

type Props = {
    actionGroups: PageActionGroup[];
    actionsLabel?: string;
    actionsAlign?: 'start' | 'center' | 'end';
    onSave: () => void | Promise<void>;
    saveDisabled?: boolean;
    saveLoading?: boolean;
    saveTitle?: string;
    saveDataTest?: string;
    /** Droits fins TOUS requis pour enregistrer. Sans eux : bouton grisé + cadenas (cf. `Button`). */
    saveRequiredPermissions?: readonly TotadminPermission[];
    className?: string;
};

/**
 * Combines a small icon-only save button with the page actions dropdown.
 * Save sits on the left, the actions menu on the right.
 *
 * The save button uses `type="button"` and calls `onSave` directly — pass
 * a react-hook-form `handleSubmit(...)` here for form-driven pages.
 */
export const ActionsAndSave = ({
    actionGroups,
    actionsLabel,
    actionsAlign,
    onSave,
    saveDisabled = false,
    saveLoading = false,
    saveTitle = 'Sauvegarder',
    saveDataTest,
    saveRequiredPermissions,
    className,
}: Props) => {
    return (
        <div className={['flex items-center gap-2', className].filter(Boolean).join(' ')}>
            <Button
                type="button"
                size="icon-lg"
                variant="default"
                title={saveTitle}
                aria-label={saveTitle}
                disabled={saveDisabled || saveLoading}
                data-test={saveDataTest}
                requiredPermissions={saveRequiredPermissions}
                onClick={() => {
                    void onSave();
                }}
            >
                {saveLoading ? <Loader2 className="animate-spin" /> : <Save />}
            </Button>
            <PageActionsMenu groups={actionGroups} label={actionsLabel} align={actionsAlign} />
        </div>
    );
};
